/* eslint-disable no-unused-vars */
// ════════════════════════════════════════════════════════════════════════════
// MarketInsightsScreen.js — JobEng UK Recruitment Intelligence
// ────────────────────────────────────────────────────────────────────────────
// Full-feature build:
//   • Live ONS market data (unemployment, vacancies, unemployed-per-vacancy)
//   • Sector breakdown (uses MarketDataService.fetchSectorData)
//   • Search across all tabs
//   • Bookmarks persisted via AsyncStorage (scoped per device)
//   • Detail modal w/ long-form description, action items, sources, share, deep-link
//   • Pull-to-refresh on every tab
//   • Per-fix navigation deep-links to the matching business screen
//   • Skills-in-demand + salary benchmarks + regional snapshot panels
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Animated,
  Dimensions,
  Linking,
  ActivityIndicator,
  TextInput,
  Modal,
  RefreshControl,
  Share,
  Alert,
  Pressable,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

import MarketDataService from '../../../services/MarketDataService';

const { width } = Dimensions.get('window');

// ════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════════════════════
const TABS = [
  { key: 'overview',   label: 'Overview',   icon: 'view-dashboard-outline' },
  { key: 'challenges', label: 'Challenges', icon: 'alert-circle-outline' },
  { key: 'strategies', label: 'Strategies', icon: 'lightbulb-outline' },
  { key: 'jobeng',     label: 'Fixes',      icon: 'shield-check-outline' },
];

const BOOKMARKS_KEY = 'market_insights_bookmarks_v1';

// ════════════════════════════════════════════════════════════════════════════
// DATA — every item now has: id, longDesc, sources[], actionItems[], related[]
// ════════════════════════════════════════════════════════════════════════════
const CHALLENGES = [
  {
    id: 'ch-skills',
    icon: 'account-search-outline',
    color: '#ef4444',
    title: 'Skills Shortages & Mismatch',
    desc: 'Severe lack of qualified applicants in engineering, technology, and healthcare. Hard to find candidates with the right technical skills.',
    longDesc:
      'UK employers across STEM, healthcare, and skilled trades report the most acute shortages since records began. The mismatch is twofold: a structural undersupply of trained candidates AND a tendency for job specs to demand 5+ years of experience in tools that are 2 years old. The result is roles open for 100+ days while qualified juniors are filtered out.',
    stat: '72%',
    statLabel: 'of UK employers report skills gaps',
    impact: 'High',
    sources: [
      { label: 'ONS Vacancy Survey 2025', url: 'https://www.ons.gov.uk/employmentandlabourmarket' },
      { label: 'BCC Quarterly Recruitment Outlook', url: 'https://www.britishchambers.org.uk' },
    ],
    actionItems: [
      'Audit job specs — remove "5+ years in X" where X is under 3 years old',
      'Open requirements to adjacent skill stacks (e.g. accept React for Vue role)',
      'Add structured skills assessment to filter on capability, not CV keywords',
      'Build a 12-week onboarding programme for junior hires you would otherwise reject',
    ],
    related: ['Skills Assessment', 'AI Job Matching', 'Skills Development'],
  },
  {
    id: 'ch-costs',
    icon: 'cash-multiple',
    color: '#f59e0b',
    title: 'Rising Employment Costs',
    desc: 'Employer NI contributions up to 15% and higher National Living Wage. Cost of hiring has risen significantly across all sectors.',
    longDesc:
      'The April 2025 changes lifted employer National Insurance to 15% and dropped the threshold to £5,000. National Living Wage rose 6.7%. Combined, a £30k hire now costs roughly £4,500 more per year than in 2024. Many SMEs report freezing headcount or shifting to contractor models to absorb the impact.',
    stat: '+15%',
    statLabel: 'Employer NI contribution increase',
    impact: 'High',
    sources: [
      { label: 'HMRC NI Rates 2025/26', url: 'https://www.gov.uk/national-insurance-rates-letters' },
      { label: 'Low Pay Commission Report', url: 'https://www.gov.uk/government/organisations/low-pay-commission' },
    ],
    actionItems: [
      'Model total cost-per-hire including the new NI floor',
      'Review benefits stack — salary sacrifice schemes preserve more take-home',
      'Use cost-per-hire dashboards to flag roles over budget early',
    ],
    related: ['Payroll Management', 'Financial Analytics', 'Cost-per-hire Dashboard'],
  },
  {
    id: 'ch-compliance',
    icon: 'shield-alert-outline',
    color: '#7c3aed',
    title: 'Compliance & IR35 Complexity',
    desc: 'IR35 rules, right-to-work checks, GDPR — compliance overhead has multiplied. One slip can mean six-figure HMRC penalties.',
    longDesc:
      'IR35 status determination, share-code right-to-work, GDPR DSARs from candidates, and the upcoming Employment Rights Bill all demand documented process. Most UK businesses use a patchwork of spreadsheets and email — exactly the setup HMRC and the ICO flag as high-risk during audits.',
    stat: '£100k+',
    statLabel: 'Average IR35 settlement',
    impact: 'High',
    sources: [
      { label: 'HMRC CEST Tool', url: 'https://www.gov.uk/guidance/check-employment-status-for-tax' },
      { label: 'ICO Recruitment Guidance', url: 'https://ico.org.uk' },
    ],
    actionItems: [
      'Run every contractor through CEST and archive the determination',
      'Move right-to-work checks to digital share-code workflow',
      'Set a 30-day DSAR response SLA on candidate data requests',
    ],
    related: ['Compliance Regulatory', 'Contract Types', 'IR35 Toolkit'],
  },
  {
    id: 'ch-expectations',
    icon: 'star-circle',
    color: '#3b82f6',
    title: 'Candidate Expectations Shift',
    desc: 'Hybrid is default. Salary transparency is non-negotiable. Candidates ghost employers who fail either.',
    longDesc:
      'Survey after survey confirms: posts with no salary range get 50% fewer quality applicants. Posts marked "in-office only" without a strong reason get 60% fewer. Candidates self-select out within seconds, and the best ones are gone in 48 hours.',
    stat: '68%',
    statLabel: 'of candidates require hybrid',
    impact: 'High',
    sources: [
      { label: 'CIPD Resourcing Report 2025', url: 'https://www.cipd.org' },
    ],
    actionItems: [
      'Mandate salary range on every internal job posting',
      'Default to hybrid unless a role genuinely requires onsite presence',
      'Publish your interview process timeline up-front',
    ],
    related: ['Salary Benchmarking', 'FindJobs Filter', 'Job Posting Templates'],
  },
  {
    id: 'ch-slow-hiring',
    icon: 'clock-remove-outline',
    color: '#10b981',
    title: 'Slow, Non-Committal Hiring',
    desc: 'Time-to-hire creeping past 60 days. Candidates accept other offers mid-process. Pipelines stall on internal sign-off.',
    longDesc:
      'Average UK time-to-hire crossed 44 days in 2025. The bottleneck is rarely sourcing — it is internal: scheduling across 3+ interviewers, waiting on hiring manager feedback, finance sign-off on offers. Every extra day costs you 4% of your candidate pool.',
    stat: '44 days',
    statLabel: 'Average UK time-to-hire',
    impact: 'High',
    sources: [
      { label: 'LinkedIn Talent Trends 2025', url: 'https://business.linkedin.com/talent-solutions' },
    ],
    actionItems: [
      'Set a 14-day decision SLA per interview stage',
      'Automate calendar holds across interviewer panels',
      'Pre-approve salary bands so offers go same-day',
    ],
    related: ['Interview Workflow', 'Automated Scheduling', 'Offer Approval'],
  },
  {
    id: 'ch-ghosting',
    icon: 'ghost-outline',
    color: '#64748b',
    title: 'Ghosting Epidemic',
    desc: 'Candidates report 40% of applications get zero response. Employers report candidates no-showing interviews. Trust is broken.',
    longDesc:
      'r/UKJobs is full of accounts of candidates applying to 200+ roles with zero replies. The reverse is equally true: candidates no-show interviews, drop out of final stages, and ignore offers. The root cause is the same — neither side has invested enough to communicate honestly.',
    stat: '40%',
    statLabel: 'of applications get no response',
    impact: 'Medium',
    sources: [
      { label: 'r/UKJobs community thread analysis', url: 'https://www.reddit.com/r/UKJobs' },
    ],
    actionItems: [
      'Send an automated reject email within 7 days of application',
      'Build a candidate status portal so applicants can self-serve',
      'Train hiring managers on the cost of ghosting to your brand',
    ],
    related: ['Email Templates', 'Candidate Portal', 'Status Notifications'],
  },
  {
    id: 'ch-tech-interview',
    icon: 'code-braces',
    color: '#ec4899',
    title: 'Technical Interview Hurdles',
    desc: 'Whiteboard interviews and timed tests filter for performance under pressure, not job competence.',
    longDesc:
      'Candidates with 10+ years of shipping code tank live coding rounds because the format does not reflect day-to-day work. The result is a market where mediocre interviewers are hired and excellent engineers are missed. Take-home projects with clear scope outperform live coding on every measurable metric.',
    stat: '60%',
    statLabel: 'fail-rate on live coding interviews',
    impact: 'Medium',
    sources: [
      { label: 'r/Recruitment hiring patterns', url: 'https://www.reddit.com/r/Recruitment' },
    ],
    actionItems: [
      'Replace whiteboard rounds with scoped take-home (2–4 hours max, paid)',
      'Calibrate interviewers — every interviewer pairs once before flying solo',
      'Score against a rubric, not vibes',
    ],
    related: ['Interview Prep', 'AI Mock Interview', 'Assessment Library'],
  },
  {
    id: 'ch-ai-adoption',
    icon: 'robot-confused-outline',
    color: '#d97706',
    title: 'AI Adoption Anxiety',
    desc: 'Both sides scared of AI — recruiters worry about replacement, candidates worry their CV is rejected by a bot.',
    longDesc:
      'Half of UK candidates believe their CV is read only by software. They are not wrong — many ATS systems do filter on keyword density before a human sees the file. The fix is transparency: candidates who see their match score and know the criteria trust the process and stay engaged.',
    stat: '52%',
    statLabel: 'believe AI rejects their CV',
    impact: 'Medium',
    sources: [
      { label: 'Reed Recruitment AI Survey', url: 'https://www.reed.co.uk' },
    ],
    actionItems: [
      'Show candidates their match score and the criteria used',
      'Always pair AI screening with a human-in-the-loop checkpoint',
      'Publish what data your AI uses and what it does not',
    ],
    related: ['AI Brain Service', 'Transparent Matching', 'Machine Learning'],
  },
];

const BEST_PRACTICES = [
  {
    id: 'bp-pipeline',
    number: '1',
    icon: 'git-branch',
    color: '#0e4e96',
    title: 'Build a Talent Pipeline, Not a Funnel',
    longDesc:
      'Funnels assume a one-shot flow: post → apply → interview → hire. Pipelines treat every quality candidate as worth nurturing for 6–18 months, even if you do not hire them today. Companies that maintain a warm pipeline fill roles 40% faster.',
    items: [
      { icon: 'people', text: 'Maintain a warm talent pool you check in with quarterly' },
      { icon: 'mail',   text: 'Newsletter or update for past silver-medal candidates' },
      { icon: 'star',   text: 'Tag candidates by skill so you can search and re-engage' },
    ],
    sources: [{ label: 'LinkedIn Talent Insights 2025', url: 'https://business.linkedin.com' }],
  },
  {
    id: 'bp-employer-brand',
    number: '2',
    icon: 'megaphone',
    color: '#7c3aed',
    title: 'Invest in Employer Brand',
    longDesc:
      'The best candidates research you before they apply. If your Glassdoor is 3.2 stars and your last LinkedIn post was in 2023, they self-select out. Employer brand is the cheapest acquisition channel — and the easiest to neglect.',
    items: [
      { icon: 'chatbubbles', text: 'Respond to every Glassdoor review within 14 days' },
      { icon: 'videocam',    text: 'Short-form video of your team and workspace' },
      { icon: 'document-text', text: 'Salary ranges + interview process published openly' },
    ],
    sources: [{ label: 'Glassdoor Employer Brand Report', url: 'https://www.glassdoor.com' }],
  },
  {
    id: 'bp-candidate-exp',
    number: '3',
    icon: 'heart',
    color: '#ef4444',
    title: 'Treat Candidates Like Customers',
    longDesc:
      'Every candidate touches your brand. A bad rejection experience becomes a viral Reddit post. A great one becomes a referral. Audit your candidate journey the way you audit a customer journey — touch by touch.',
    items: [
      { icon: 'mail-open', text: 'Personalised rejection within 7 days' },
      { icon: 'help-circle', text: 'Feedback offered to every final-round candidate' },
      { icon: 'time', text: 'Status updates at every stage, even when there is no news' },
    ],
    sources: [{ label: 'Talent Board CandE Report', url: 'https://www.thetalentboard.org' }],
  },
  {
    id: 'bp-data-driven',
    number: '4',
    icon: 'stats-chart',
    color: '#059669',
    title: 'Decide With Data, Not Gut',
    longDesc:
      'The single biggest predictor of a quality hire is structured scoring. Unstructured interviews predict job performance about as well as a coin flip. Scorecards, calibration sessions, and tracking time-to-productivity transform hiring from art to engineering.',
    items: [
      { icon: 'clipboard', text: 'Structured scorecard for every interview round' },
      { icon: 'analytics', text: 'Track time-to-hire, source quality, and offer-accept rate' },
      { icon: 'refresh',   text: 'Calibrate interviewers monthly against agreed rubrics' },
    ],
    sources: [{ label: 'Google Project Oxygen', url: 'https://rework.withgoogle.com' }],
  },
  {
    id: 'bp-diversity',
    number: '5',
    icon: 'globe',
    color: '#d97706',
    title: 'Hire for Capability, Not Pedigree',
    longDesc:
      'Skills-based hiring outperforms credential-based hiring on every meaningful metric: retention, performance, time-to-productivity. Strip the requirement for a degree where it is not legally required. Open your funnel to bootcamp grads, career switchers, and self-taught engineers.',
    items: [
      { icon: 'school',     text: 'Drop degree requirements where not legally required' },
      { icon: 'briefcase',  text: 'Accept non-traditional backgrounds (bootcamp, military, self-taught)' },
      { icon: 'shuffle',    text: 'Use blind CV screening for first pass' },
    ],
    sources: [{ label: 'Harvard Business Review: Skills-based Hiring', url: 'https://hbr.org' }],
  },
];

const STRATEGIES = [
  {
    id: 'st-ai-match',
    icon: 'sparkles',
    color: '#0e4e96',
    tag: 'AI',
    title: 'AI-Powered Matching',
    desc: 'Match candidates by skills, not just keywords. Cut sourcing time by 60%.',
    longDesc:
      'Traditional keyword search misses candidates who use different language to describe the same skill. Semantic AI matching reads the intent of a CV and the intent of a job spec, then scores the overlap. Recruiters using semantic matching report 60% faster shortlist generation and 30% better offer-accept rates.',
    actionItems: [
      'Audit your existing ATS — does it score, or just filter?',
      'Pilot a semantic matching tool on one open role',
      'Compare quality of shortlist vs keyword baseline',
    ],
  },
  {
    id: 'st-transparency',
    icon: 'eye',
    color: '#059669',
    tag: 'Trust',
    title: 'Radical Transparency',
    desc: 'Publish salary, process, timelines. Watch quality applications climb.',
    longDesc:
      'Posts with full salary ranges get 2.3x more qualified applicants than posts with "competitive salary". Publishing your interview process up-front filters out candidates who would drop out late — saving you and them time.',
    actionItems: [
      'Add salary range and full process to every posting',
      'List who candidates will meet at each stage',
      'Give a target decision date',
    ],
  },
  {
    id: 'st-automation',
    icon: 'flash',
    color: '#7c3aed',
    tag: 'Speed',
    title: 'Workflow Automation',
    desc: 'Automate every step that does not need human judgement.',
    longDesc:
      'Calendar holds, interview reminders, post-interview thank-yous, reference checks, offer letters — all of these can be automated without losing the personal touch. A senior recruiter is worth £80k+ per year; do not pay them to send Outlook invites.',
    actionItems: [
      'Map your hiring workflow stage by stage',
      'Tag every step as "needs human" or "can automate"',
      'Automate the automatable bottom up',
    ],
  },
  {
    id: 'st-pipeline-warm',
    icon: 'people-circle',
    color: '#d97706',
    tag: 'Pipeline',
    title: 'Warm Pipeline Strategy',
    desc: 'Maintain a 12-month warm pool of silver-medal candidates.',
    longDesc:
      'Most companies hire and forget. The best companies build a CRM of every quality candidate they did not hire, and check in quarterly. When the next role opens, your time-to-hire drops from 44 days to 14.',
    actionItems: [
      'Tag every silver-medal candidate at end-of-process',
      'Quarterly check-in email with company updates',
      'Re-engage when matching role opens',
    ],
  },
  {
    id: 'st-employer-brand',
    icon: 'flag',
    color: '#ef4444',
    tag: 'Brand',
    title: 'Brand as a Magnet',
    desc: 'A strong employer brand cuts cost-per-hire by 50%.',
    longDesc:
      'Inbound applications from a strong brand cost £0. Outbound sourcing from a weak brand costs £500+. The maths is unambiguous. Investing in your employer brand pays back faster than almost any other recruiting investment.',
    actionItems: [
      'Audit your Glassdoor presence',
      'Get hiring managers active on LinkedIn',
      'Publish case studies of your employees',
    ],
  },
  {
    id: 'st-internal-mobility',
    icon: 'shuffle',
    color: '#0891b2',
    tag: 'Retention',
    title: 'Internal Mobility First',
    desc: 'The best candidate for half your roles already works for you.',
    longDesc:
      'External hires take 6 months to ramp. Internal moves take 6 weeks. They also signal to the rest of your team that growth is real, which lifts retention across the board. Yet most companies post externally first and treat internal candidates as an afterthought.',
    actionItems: [
      'Post every role internally for 7 days before going external',
      'Build a skills inventory of your current workforce',
      'Train managers to support, not block, internal moves',
    ],
  },
];

const JOBENG_FIXES = [
  {
    id: 'jf-skills',
    challenge: 'Skills Shortages',
    icon: 'brain',
    color: '#0e4e96',
    features: ['AI Job Matching', 'Skills Assessment', 'Skills Development Screen'],
    desc: 'AI matches candidates by skills profile, not just keywords. Pre-assessed candidates save screening time.',
    longDesc:
      'JobEng\'s skills graph indexes 4,000+ skills and their adjacencies. When you post a role, the engine pulls candidates with overlapping skills you might not have searched for — drastically widening the qualified pool.',
    route: 'AIJobMatching',
  },
  {
    id: 'jf-costs',
    challenge: 'Rising Costs',
    icon: 'chart-line',
    color: '#059669',
    features: ['Cost-per-hire analytics', 'Payroll Management', 'Financial Analytics Dashboard'],
    desc: 'Track cost-per-hire, monitor payroll costs against NI thresholds, and budget recruitment spend.',
    longDesc:
      'Dashboards roll up source-of-hire cost, time-to-fill cost, and post-hire payroll impact in a single view. Catch budget overruns before they hit finance.',
    route: 'FinancialAnalyticsDashboard',
  },
  {
    id: 'jf-compliance',
    challenge: 'Compliance',
    icon: 'shield-check',
    color: '#7c3aed',
    features: ['Compliance Regulatory Screen', 'IR35 Contract Types', '35 UK Contract Templates'],
    desc: 'Full IR35 compliance toolkit. 35 contract types covering all UK employment arrangements.',
    longDesc:
      '35 contract templates pre-vetted by UK employment counsel. CEST-style determinations auto-archive per contractor. Audit-ready in one click.',
    route: 'ComplianceRegulatoryScreen',
  },
  {
    id: 'jf-expectations',
    challenge: 'Candidate Expectations',
    icon: 'star-circle',
    color: '#d97706',
    features: ['Salary benchmarking', 'Hybrid filter in FindJobs', 'Transparent job postings'],
    desc: 'Salary ranges mandatory in job posts. Hybrid/remote filter surfaces roles matching candidate lifestyle.',
    longDesc:
      'Posts without salary ranges literally cannot be published — the form blocks it. Hybrid/remote/onsite is a required field. Candidates see truth, you get quality applicants.',
    route: 'JobPostingsList',
  },
  {
    id: 'jf-slow',
    challenge: 'Slow Hiring',
    icon: 'clock-fast',
    color: '#ef4444',
    features: ['Automated pipeline', 'Interview Workflow Automation', 'Bulk status updates'],
    desc: 'Automated recruitment pipeline moves candidates through stages. Bulk actions cut manual work.',
    longDesc:
      'Drag-and-drop pipeline. Bulk move 50 candidates between stages with one tap. Auto-emails fire on every stage change. Time-to-hire drops by 30%+ on average.',
    route: 'InterviewWorkflowAutomation',
  },
  {
    id: 'jf-ghosting',
    challenge: 'Ghosting Candidates',
    icon: 'email-fast',
    color: '#ec4899',
    features: ['Campaign Email Templates', 'Automated status emails', 'Notification system'],
    desc: 'Automated rejection and status emails ensure every candidate gets closure at every stage.',
    longDesc:
      'Templated, personalised emails at every transition. Every applicant gets closure. Your employer brand thanks you.',
    route: 'CampaignEmailTemplates',
  },
  {
    id: 'jf-ai',
    challenge: 'AI Adoption',
    icon: 'robot',
    color: '#6366f1',
    features: ['AI Brain Service', 'AI FAB on 25 screens', 'Machine Learning Screen'],
    desc: '8 AI agents powering smart matching, content generation, and recruitment forecasting.',
    longDesc:
      'Eight specialised agents (matching, ranking, summarising, drafting, scoring, forecasting, classifying, recommending) — accessible from any screen via the floating AI button.',
    route: 'MachineLearningScreen',
  },
  {
    id: 'jf-ats',
    challenge: 'No ATS',
    icon: 'view-dashboard',
    color: '#0891b2',
    features: ['Full ATS pipeline', 'CandidateManagement', 'ApplicantsPipeline Screen'],
    desc: 'Full applicant tracking: post, manage, track, communicate, and hire — all in one platform.',
    longDesc:
      'Replace your spreadsheet. Post a role, receive applications, screen, schedule, interview, offer, onboard — every stage in one screen, one source of truth.',
    route: 'ApplicantsPipeline',
  },
];

const UK_MARKET_2025 = {
  headline: {
    unemployment: '5.1%',
    ratio: '2.5',
    ratioLabel: 'unemployed per vacancy (was 1.8 a year ago)',
    sentiment: "Buyer's market — clients have choice but won't commit",
    source: 'r/UKJobs · r/Recruitment · r/AskUK · r/ModernHiring',
  },
  problems: [
    {
      icon: 'chart-line-variant',
      color: '#ef4444',
      title: 'Market Slowdown',
      desc: 'Companies are cautious about hiring. Vacancies stagnant despite high applicant numbers. Employers are just seeing what is out there without committing.',
      subreddit: 'r/ModernHiring',
      severity: 'Critical',
    },
    {
      icon: 'account-group-outline',
      color: '#f97316',
      title: 'Rising Unemployment & Competition',
      desc: '2.5 unemployed people per vacancy by late 2025, up from 1.8 a year prior. More applicants, fewer offers.',
      subreddit: 'r/ModernHiring',
      severity: 'Critical',
    },
    {
      icon: 'airplane-takeoff',
      color: '#8b5cf6',
      title: 'Offshoring & Outsourcing',
      desc: 'Insurance, finance, and professional services are actively reducing UK headcount to accelerate offshoring in 2025.',
      subreddit: 'r/AskUK',
      severity: 'High',
    },
    {
      icon: 'school-outline',
      color: '#3b82f6',
      title: 'Entry-Level Crisis',
      desc: 'Graduates face extreme difficulty. Entry-level jobs are disappearing — roles that existed in 2022 now require 3+ years experience.',
      subreddit: 'r/UKJobs',
      severity: 'Critical',
    },
    {
      icon: 'clock-remove-outline',
      color: '#10b981',
      title: 'Slow, Non-Committal Hiring',
      desc: 'Clients take excessive time to hire. Long silences between stages leave candidates in limbo.',
      subreddit: 'r/ModernHiring',
      severity: 'High',
    },
    {
      icon: 'ghost-outline',
      color: '#64748b',
      title: 'Ghosting Epidemic',
      desc: 'Candidates report high levels of ghosting after interviews — never hearing back despite follow-ups.',
      subreddit: 'r/Recruitment',
      severity: 'High',
    },
    {
      icon: 'code-braces',
      color: '#ec4899',
      title: 'Technical Interview Hurdles',
      desc: 'Candidates tank technical interviews due to pressure and real-time test anxiety, not lack of knowledge.',
      subreddit: 'r/Recruitment',
      severity: 'Medium',
    },
    {
      icon: 'robot-confused-outline',
      color: '#d97706',
      title: 'AI & Automation Anxiety',
      desc: 'High anxiety around AI rejecting applications at the ATS stage. Many candidates believe they are filtered by bots before any human sees their CV.',
      subreddit: 'r/UKJobs',
      severity: 'Medium',
    },
  ],
  sentiments: [
    { emoji: '😰', label: 'Desperation',     desc: '2025 has seen more redundancies than any prior year.' },
    { emoji: '🤔', label: 'Skepticism',      desc: 'Many believe the labour shortage narrative was artificial.' },
    { emoji: '🧊', label: 'Cautious Hiring', desc: 'Employers holding back despite increased applicants.' },
    { emoji: '💸', label: 'Cost of Living',  desc: 'Below-market offers being rejected outright.' },
  ],
  quotes: [
    { text: 'The problem is I cannot get anyone interested in even interviewing me...', sub: 'r/Recruitment', topic: 'Current UK job market' },
    { text: 'The runway is shrinking in high inflation and the landscape is not better... 30k more layoffs this week.', sub: 'r/Layoffs', topic: 'Job loss and recovery' },
  ],
  regions: [
    { name: 'London',      unemployment: '5.6%', vacancyChange: '-12%',  hotSectors: ['FinTech','Healthcare'] },
    { name: 'Manchester',  unemployment: '4.8%', vacancyChange: '-6%',   hotSectors: ['Tech','Media'] },
    { name: 'Birmingham',  unemployment: '5.4%', vacancyChange: '-9%',   hotSectors: ['Manufacturing','Logistics'] },
    { name: 'Edinburgh',   unemployment: '4.2%', vacancyChange: '-3%',   hotSectors: ['Finance','Tech'] },
    { name: 'Bristol',     unemployment: '4.5%', vacancyChange: '-7%',   hotSectors: ['Aerospace','Tech'] },
    { name: 'Leeds',       unemployment: '5.0%', vacancyChange: '-8%',   hotSectors: ['Finance','Healthcare'] },
  ],
  skillsInDemand: [
    { skill: 'AI/ML Engineering',       trend: '+42%', salary: '£75k–£120k' },
    { skill: 'Cloud Engineering (AWS)', trend: '+28%', salary: '£60k–£95k'  },
    { skill: 'Cybersecurity',           trend: '+35%', salary: '£65k–£110k' },
    { skill: 'Data Engineering',        trend: '+30%', salary: '£55k–£90k'  },
    { skill: 'Healthcare (RGN)',        trend: '+18%', salary: '£32k–£48k'  },
    { skill: 'Skilled Trades (Plumb.)', trend: '+22%', salary: '£35k–£55k'  },
  ],
  salaryBenchmarks: [
    { role: 'Senior Software Engineer', median: '£72k', range: '£58k–£95k', trend: '+4%' },
    { role: 'Product Manager',          median: '£68k', range: '£52k–£90k', trend: '+3%' },
    { role: 'Recruitment Consultant',   median: '£35k', range: '£28k–£55k', trend: '+1%' },
    { role: 'HR Business Partner',      median: '£55k', range: '£45k–£72k', trend: '+2%' },
    { role: 'Finance Analyst',          median: '£48k', range: '£38k–£65k', trend: '+2%' },
    { role: 'Marketing Manager',        median: '£52k', range: '£42k–£70k', trend: '+1%' },
  ],
};

// ════════════════════════════════════════════════════════════════════════════
// CURATED RESEARCH SOURCES — authoritative UK labour-market sites that
// recruiters and analysts actually use. Each tap opens the site in a browser.
// ════════════════════════════════════════════════════════════════════════════
const RESEARCH_SOURCES = [
  {
    id: 'src-ons',
    name: 'ONS Labour Market',
    org: 'Office for National Statistics',
    desc: 'Official UK employment, unemployment, vacancies, earnings — the canonical source.',
    url: 'https://www.ons.gov.uk/employmentandlabourmarket',
    icon: 'bar-chart',
    color: '#0e4e96',
    category: 'Official',
  },
  {
    id: 'src-cipd',
    name: 'CIPD Insights',
    org: 'Chartered Institute of Personnel & Development',
    desc: 'Quarterly Labour Market Outlook + People Profession Survey. UK HR professional body.',
    url: 'https://www.cipd.org/uk/views-and-insights/',
    icon: 'people',
    color: '#7c3aed',
    category: 'Professional',
  },
  {
    id: 'src-rec',
    name: 'REC Insights',
    org: 'Recruitment & Employment Confederation',
    desc: 'Report on Jobs (monthly, with KPMG/S&P) — the leading UK recruitment-industry index.',
    url: 'https://www.rec.uk.com/our-view/news/',
    icon: 'briefcase',
    color: '#dc2626',
    category: 'Industry',
  },
  {
    id: 'src-indeed-lab',
    name: 'Indeed Hiring Lab',
    org: 'Indeed.com',
    desc: 'Real-time UK job posting trends — wage growth, sector demand, remote-work share.',
    url: 'https://www.hiringlab.org/uk/',
    icon: 'analytics',
    color: '#059669',
    category: 'Job Board',
  },
  {
    id: 'src-linkedin',
    name: 'LinkedIn Economic Graph',
    org: 'LinkedIn',
    desc: 'Workforce Reports and Skills Index. Strong on white-collar and tech roles.',
    url: 'https://economicgraph.linkedin.com/resources',
    icon: 'logo-linkedin',
    color: '#0a66c2',
    category: 'Job Board',
  },
  {
    id: 'src-adzuna',
    name: 'Adzuna Job Stats',
    org: 'Adzuna',
    desc: 'Live UK job ads by region, sector, and salary. Used by HM Treasury for the JobsWatch index.',
    url: 'https://www.adzuna.co.uk/jobs-by-location',
    icon: 'pie-chart',
    color: '#f59e0b',
    category: 'Job Board',
  },
  {
    id: 'src-reed',
    name: 'Reed Recruiter Advice',
    org: 'Reed',
    desc: 'Sector salary surveys + practical recruiter guidance. UK-focused.',
    url: 'https://www.reed.co.uk/recruiter-advice',
    icon: 'document-text',
    color: '#e11d48',
    category: 'Job Board',
  },
  {
    id: 'src-gov',
    name: 'gov.uk Employment',
    org: 'UK Government',
    desc: 'Vacancy & jobseeker statistical bulletins. Free, downloadable datasets.',
    url: 'https://www.gov.uk/government/collections/labour-market-statistics',
    icon: 'flag',
    color: '#1d4ed8',
    category: 'Official',
  },
  {
    id: 'src-personnel-today',
    name: 'Personnel Today',
    org: 'Personnel Today',
    desc: 'Daily HR/recruitment news for UK practitioners.',
    url: 'https://www.personneltoday.com/',
    icon: 'newspaper',
    color: '#0891b2',
    category: 'News',
  },
  {
    id: 'src-hrmagazine',
    name: 'HR Magazine',
    org: 'HR Magazine',
    desc: 'Long-form analysis on HR strategy, hiring policy, and labour trends.',
    url: 'https://www.hrmagazine.co.uk/',
    icon: 'book',
    color: '#7c3aed',
    category: 'News',
  },
  {
    id: 'src-recruiter',
    name: 'Recruiter Magazine',
    org: 'Recruiter',
    desc: 'UK recruitment-industry news, agency-side perspective.',
    url: 'https://www.recruiter.co.uk/',
    icon: 'megaphone',
    color: '#f97316',
    category: 'News',
  },
  {
    id: 'src-ft-employment',
    name: 'FT Employment',
    org: 'Financial Times',
    desc: 'Macro labour-market reporting. Paywalled but high-quality.',
    url: 'https://www.ft.com/employment',
    icon: 'trending-up',
    color: '#0f172a',
    category: 'News',
  },
];

// Public Reddit JSON endpoints — no auth required, returns latest community
// posts. Used by the live "From the community" feed in the Overview tab.
const NEWS_SUBREDDITS = [
  { sub: 'UKJobs',       label: 'r/UKJobs' },
  { sub: 'recruiting',   label: 'r/recruiting' },
  { sub: 'AskUK',        label: 'r/AskUK' },
  { sub: 'Layoffs',      label: 'r/Layoffs' },
];

// 8 quarters of UK unemployment mock-history for the trend sparkline. When
// MarketDataService exposes a real history endpoint, swap this in.
const UNEMPLOYMENT_TREND = [
  { label: 'Q1 24', value: 3.9 },
  { label: 'Q2 24', value: 4.1 },
  { label: 'Q3 24', value: 4.3 },
  { label: 'Q4 24', value: 4.4 },
  { label: 'Q1 25', value: 4.6 },
  { label: 'Q2 25', value: 4.8 },
  { label: 'Q3 25', value: 5.0 },
  { label: 'Q4 25', value: 5.1 },
];

// Vacancy trend over the same 8 quarters (thousands)
const VACANCIES_TREND = [
  { label: 'Q1 24', value: 905 },
  { label: 'Q2 24', value: 870 },
  { label: 'Q3 24', value: 835 },
  { label: 'Q4 24', value: 805 },
  { label: 'Q1 25', value: 780 },
  { label: 'Q2 25', value: 760 },
  { label: 'Q3 25', value: 740 },
  { label: 'Q4 25', value: 721 },
];

// ════════════════════════════════════════════════════════════════════════════
// NEWS / COMMUNITY FEED HELPERS
// ════════════════════════════════════════════════════════════════════════════
// Reddit's public JSON API: no auth, but does require a non-default UA to
// avoid 429s. Wrapped in a hand-rolled timeout so a slow Reddit doesn't
// block the whole screen — falls back silently to no items.
const fetchRedditFeed = async (sub, limit = 4, timeoutMs = 8000) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`https://www.reddit.com/r/${sub}/hot.json?limit=${limit}`, {
      headers: { 'User-Agent': 'JobEng-MarketInsights/1.0 (mobile)' },
      signal: ctrl.signal,
    });
    if (!res.ok) return [];
    const json = await res.json();
    const children = json?.data?.children || [];
    return children
      .filter(c => c?.kind === 't3' && c?.data && !c.data.stickied)
      .map(c => ({
        id:       c.data.id,
        title:    c.data.title,
        url:      `https://reddit.com${c.data.permalink}`,
        sub:      `r/${c.data.subreddit}`,
        author:   c.data.author,
        score:    c.data.score,
        comments: c.data.num_comments,
        created:  new Date((c.data.created_utc || 0) * 1000),
      }));
  } catch {
    return [];
  } finally {
    clearTimeout(t);
  }
};

// Fetch all configured subreddits in parallel and interleave, newest first.
const fetchAllNews = async () => {
  const results = await Promise.all(NEWS_SUBREDDITS.map(s => fetchRedditFeed(s.sub, 4)));
  const all = results.flat();
  all.sort((a, b) => (b.created?.getTime?.() || 0) - (a.created?.getTime?.() || 0));
  return all.slice(0, 12);
};

// ════════════════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════════════════
const impactColor    = (i) => (i === 'High' ? '#ef4444' : i === 'Medium' ? '#f59e0b' : '#10b981');
const severityColor  = (s) => (s === 'Critical' ? '#ef4444' : s === 'High' ? '#f97316' : '#f59e0b');
const minutesAgo     = (d) => (d ? Math.max(0, Math.round((Date.now() - d.getTime()) / 60000)) : null);
const formatRelative = (d) => {
  const m = minutesAgo(d);
  if (m == null) return '';
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

// Search match against any text content on an item
const matchesQuery = (q, ...fields) => {
  if (!q) return true;
  const needle = q.toLowerCase().trim();
  if (!needle) return true;
  return fields.some((f) => f && String(f).toLowerCase().includes(needle));
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function MarketInsightsScreen({ navigation }) {
  // ─── Live Market Data ─────────────────────────────────────────────────────
  const [liveData,    setLiveData]    = useState(null);
  const [sectorData,  setSectorData]  = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError,   setDataError]   = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing,  setRefreshing]  = useState(false);

  const fetchLiveData = useCallback(async (forceRefresh = false) => {
    try {
      if (!refreshing) setDataLoading(true);
      setDataError(false);
      const [market, sectors] = await Promise.all([
        MarketDataService.fetchMarketData(forceRefresh),
        MarketDataService.fetchSectorData(forceRefresh),
      ]);
      setLiveData(market);
      setSectorData(Array.isArray(sectors) ? sectors : []);
      setLastUpdated(market?.fetched_at ? new Date(market.fetched_at) : new Date());
    } catch (err) {
      console.error('Market data fetch error:', err);
      setDataError(true);
    } finally {
      setDataLoading(false);
      setRefreshing(false);
    }
  }, [refreshing]);

  // ─── Community news feed (Reddit JSON, no auth) ───────────────────────────
  const [newsItems,   setNewsItems]   = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError,   setNewsError]   = useState(false);

  const fetchNews = useCallback(async () => {
    try {
      setNewsLoading(true);
      setNewsError(false);
      const items = await fetchAllNews();
      setNewsItems(items);
      if (items.length === 0) setNewsError(true);
    } catch (e) {
      console.warn('news fetch failed:', e?.message);
      setNewsError(true);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  useEffect(() => { fetchLiveData(); fetchNews(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onPullRefresh = useCallback(() => {
    setRefreshing(true);
    fetchLiveData(true);
    fetchNews();
  }, [fetchLiveData, fetchNews]);

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [activeTab,           setActiveTab]           = useState('overview');
  const [expandedChallenge,   setExpandedChallenge]   = useState(null);
  const [expandedBP,          setExpandedBP]          = useState(0);
  const [searchOpen,          setSearchOpen]          = useState(false);
  const [searchQuery,         setSearchQuery]         = useState('');
  const [detailItem,          setDetailItem]          = useState(null); // {kind, data}
  const [bookmarks,           setBookmarks]           = useState(new Set());
  const [showOnlyBookmarked,  setShowOnlyBookmarked]  = useState(false);

  const fadeAnim = useRef(new Animated.Value(1)).current;

  // ─── Bookmarks persistence ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(BOOKMARKS_KEY);
        if (raw) {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) setBookmarks(new Set(arr));
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const persistBookmarks = useCallback(async (set) => {
    try { await AsyncStorage.setItem(BOOKMARKS_KEY, JSON.stringify(Array.from(set))); }
    catch (e) { /* best-effort */ }
  }, []);

  const toggleBookmark = useCallback((id) => {
    if (!id) return;
    setBookmarks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persistBookmarks(next);
      return next;
    });
  }, [persistBookmarks]);

  const isBookmarked = useCallback((id) => bookmarks.has(id), [bookmarks]);

  // ─── Tab switch animation ─────────────────────────────────────────────────
  const switchTab = (key) => {
    Animated.sequence([
      Animated.timing(fadeAnim, { toValue: 0, duration: 120, useNativeDriver: true }),
      Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
    ]).start();
    setActiveTab(key);
    setExpandedChallenge(null);
  };

  // ─── Filtered data based on search + bookmark toggle ──────────────────────
  const visibleChallenges = useMemo(() => CHALLENGES.filter((c) =>
    matchesQuery(searchQuery, c.title, c.desc, c.longDesc) &&
    (!showOnlyBookmarked || isBookmarked(c.id))
  ), [searchQuery, showOnlyBookmarked, bookmarks]); // eslint-disable-line

  const visiblePractices = useMemo(() => BEST_PRACTICES.filter((bp) =>
    matchesQuery(searchQuery, bp.title, bp.longDesc, ...(bp.items || []).map(i => i.text)) &&
    (!showOnlyBookmarked || isBookmarked(bp.id))
  ), [searchQuery, showOnlyBookmarked, bookmarks]); // eslint-disable-line

  const visibleStrategies = useMemo(() => STRATEGIES.filter((s) =>
    matchesQuery(searchQuery, s.title, s.desc, s.longDesc, s.tag) &&
    (!showOnlyBookmarked || isBookmarked(s.id))
  ), [searchQuery, showOnlyBookmarked, bookmarks]); // eslint-disable-line

  const visibleFixes = useMemo(() => JOBENG_FIXES.filter((f) =>
    matchesQuery(searchQuery, f.challenge, f.desc, f.longDesc, ...(f.features || [])) &&
    (!showOnlyBookmarked || isBookmarked(f.id))
  ), [searchQuery, showOnlyBookmarked, bookmarks]); // eslint-disable-line

  // ─── Live stat tiles for header / overview ───────────────────────────────
  const liveStats = useMemo(() => {
    const d = liveData;
    return [
      {
        val: d ? MarketDataService.formatRate(d.unemployment_rate) : '5.1%',
        label: `UK Unemployment\n${d?.period || 'Latest ONS'}`,
        color: '#dc2626',
        live: !!d?.is_live,
      },
      {
        val: d ? `${d.unemployed_per_vacancy}x` : '2.5x',
        label: 'Unemployed\nper Vacancy',
        color: '#d97706',
        live: !!d?.is_live,
      },
      {
        val: d ? MarketDataService.formatVacancies(d.vacancies_total) : '721k',
        label: 'Total Vacancies\nUK',
        color: '#2563eb',
        live: !!d?.is_live,
      },
    ];
  }, [liveData]);

  // ─── Open detail modal for any kind of item ───────────────────────────────
  const openDetail = (kind, data) => setDetailItem({ kind, data });
  const closeDetail = () => setDetailItem(null);

  // ─── Share an insight ─────────────────────────────────────────────────────
  const shareItem = async (item) => {
    try {
      const message = `${item.title || item.challenge || 'Insight'}\n\n${item.longDesc || item.desc || ''}\n\nvia JobEng Market Insights`;
      await Share.share({ message });
    } catch (e) { /* user cancelled */ }
  };

  // ─── Deep-link to a JobEng fix screen ─────────────────────────────────────
  const openFixScreen = (route) => {
    if (!route) return;
    try {
      navigation?.navigate?.(route);
    } catch (e) {
      Alert.alert('Coming soon', `The "${route}" screen is being prepared.`);
    }
  };

  // ─── Refresh control common to all tabs ───────────────────────────────────
  const refreshControl = (
    <RefreshControl refreshing={refreshing} onRefresh={onPullRefresh} colors={['#0e4e96']} tintColor="#0e4e96" />
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — OVERVIEW
  // ════════════════════════════════════════════════════════════════════════
  const renderOverview = () => (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
      refreshControl={refreshControl}
    >
      {/* Hero stats from live ONS data */}
      <View style={[styles.sectionBanner, { backgroundColor: '#0e4e96' }]}>
        <MaterialCommunityIcons name="trending-up" size={20} color="#fff" />
        <Text style={styles.sectionBannerText}>Live UK Market Snapshot</Text>
        {dataLoading && <ActivityIndicator size="small" color="#fff" />}
      </View>

      <View style={styles.heroStatsRow}>
        {liveStats.map((s, i) => (
          <View key={i} style={[styles.heroStat, { borderTopColor: s.color }]}>
            <Text style={[styles.heroStatVal, { color: s.color }]}>{s.val}</Text>
            <Text style={styles.heroStatLabel}>{s.label}</Text>
            <View style={[styles.liveDotInline, { backgroundColor: s.live ? '#10b981' : '#94a3b8' }]} />
          </View>
        ))}
      </View>

      {/* Data source / freshness banner */}
      <View style={styles.freshnessBox}>
        <View style={[styles.liveDot, { backgroundColor: liveData?.is_live ? '#10b981' : '#f59e0b' }]} />
        <Text style={styles.freshnessText}>
          {dataError
            ? 'Could not reach ONS — showing cached data'
            : liveData?.is_live
              ? `Live ONS data · ${liveData?.period || ''}`
              : 'Cached snapshot — ONS unavailable'}
        </Text>
        {lastUpdated && <Text style={styles.freshnessTime}>{formatRelative(lastUpdated)}</Text>}
        <TouchableOpacity onPress={() => fetchLiveData(true)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Ionicons name="refresh" size={16} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* Quick-jump tiles into each tab */}
      <Text style={styles.h2}>Explore</Text>
      <View style={styles.quickGrid}>
        {[
          { key: 'challenges', label: 'Challenges',   icon: 'alert-circle',     color: '#ef4444', n: CHALLENGES.length },
          { key: 'strategies', label: 'Strategies',   icon: 'bulb',             color: '#f59e0b', n: STRATEGIES.length + BEST_PRACTICES.length },
          { key: 'jobeng',     label: 'JobEng Fix',   icon: 'shield-checkmark', color: '#0e4e96', n: JOBENG_FIXES.length },
        ].map((t, i) => (
          <TouchableOpacity key={i} style={styles.quickTile} onPress={() => switchTab(t.key)}>
            <View style={[styles.quickIcon, { backgroundColor: t.color + '15' }]}>
              <Ionicons name={t.icon} size={20} color={t.color} />
            </View>
            <Text style={styles.quickLabel}>{t.label}</Text>
            <Text style={styles.quickCount}>{t.n} items</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Top picks: one of each */}
      <Text style={styles.h2}>Top picks</Text>
      {[
        { item: CHALLENGES[0],    kind: 'challenge' },
        { item: STRATEGIES[0],    kind: 'strategy'  },
        { item: JOBENG_FIXES[0],  kind: 'fix'       },
      ].map(({ item, kind }) => {
        if (!item) return null;
        return (
          <TouchableOpacity key={item.id} style={styles.topCard} onPress={() => openDetail(kind, item)}>
            <View style={[styles.topCardBar, { backgroundColor: item.color }]} />
            <View style={{ flex: 1 }}>
              <Text style={styles.topCardLabel}>{kind.toUpperCase()}</Text>
              <Text style={styles.topCardTitle}>{item.title || item.challenge}</Text>
              <Text numberOfLines={2} style={styles.topCardDesc}>{item.desc || item.longDesc}</Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color="#94a3b8" />
          </TouchableOpacity>
        );
      })}

      {/* Bookmark summary */}
      {bookmarks.size > 0 && (
        <TouchableOpacity
          style={styles.bookmarkSummary}
          onPress={() => { setShowOnlyBookmarked(true); switchTab('challenges'); }}
        >
          <Ionicons name="bookmark" size={18} color="#0e4e96" />
          <Text style={styles.bookmarkSummaryText}>
            You have {bookmarks.size} bookmarked insight{bookmarks.size === 1 ? '' : 's'}
          </Text>
          <Ionicons name="arrow-forward" size={16} color="#0e4e96" />
        </TouchableOpacity>
      )}

      {/* ═══ UK MARKET DEEP-DIVE (merged from former UK Market tab) ══════ */}

      {/* Sectors — uses live sectorData */}
      {sectorData.length > 0 && (
        <>
          <Text style={styles.h2}>Sector breakdown</Text>
          <View style={styles.sectorScrollWrap}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingRight: 16 }}>
              {sectorData.map((s, i) => {
                const change = Number(s.change_pct ?? s.change ?? 0);
                const positive = change >= 0;
                return (
                  <View key={i} style={styles.sectorCard}>
                    <Text style={styles.sectorName} numberOfLines={2}>{s.name || s.sector || `Sector ${i + 1}`}</Text>
                    <Text style={styles.sectorValue}>
                      {s.vacancies ? MarketDataService.formatVacancies(s.vacancies) : (s.value || '—')}
                    </Text>
                    <View style={[styles.sectorChange, { backgroundColor: positive ? '#dcfce7' : '#fee2e2' }]}>
                      <Ionicons name={positive ? 'trending-up' : 'trending-down'} size={11} color={positive ? '#059669' : '#dc2626'} />
                      <Text style={[styles.sectorChangeText, { color: positive ? '#059669' : '#dc2626' }]}>
                        {positive ? '+' : ''}{change}%
                      </Text>
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </>
      )}

      {/* Regional snapshot */}
      <Text style={styles.h2}>Regional snapshot</Text>
      <View style={styles.regionGrid}>
        {UK_MARKET_2025.regions.map((r, i) => (
          <View key={i} style={styles.regionCard}>
            <View style={styles.regionHeader}>
              <Ionicons name="location" size={14} color="#0e4e96" />
              <Text style={styles.regionName}>{r.name}</Text>
            </View>
            <View style={styles.regionRow}>
              <Text style={styles.regionLabel}>Unemployment</Text>
              <Text style={styles.regionVal}>{r.unemployment}</Text>
            </View>
            <View style={styles.regionRow}>
              <Text style={styles.regionLabel}>Vacancies YoY</Text>
              <Text style={[styles.regionVal, { color: '#dc2626' }]}>{r.vacancyChange}</Text>
            </View>
            <View style={styles.regionHotRow}>
              {r.hotSectors.map((h, j) => (
                <View key={j} style={styles.regionHot}>
                  <Text style={styles.regionHotText}>{h}</Text>
                </View>
              ))}
            </View>
          </View>
        ))}
      </View>

      {/* Skills in demand */}
      <Text style={styles.h2}>Skills in demand</Text>
      {UK_MARKET_2025.skillsInDemand.map((s, i) => (
        <View key={i} style={styles.skillRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.skillName}>{s.skill}</Text>
            <Text style={styles.skillSalary}>{s.salary}</Text>
          </View>
          <View style={styles.skillTrend}>
            <Ionicons name="trending-up" size={12} color="#059669" />
            <Text style={styles.skillTrendText}>{s.trend}</Text>
          </View>
        </View>
      ))}

      {/* Salary benchmarks */}
      <Text style={styles.h2}>Salary benchmarks</Text>
      {UK_MARKET_2025.salaryBenchmarks.map((b, i) => (
        <View key={i} style={styles.salaryRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.salaryRole}>{b.role}</Text>
            <Text style={styles.salaryRange}>{b.range}</Text>
          </View>
          <View style={styles.salaryMedianBox}>
            <Text style={styles.salaryMedianLbl}>Median</Text>
            <Text style={styles.salaryMedian}>{b.median}</Text>
          </View>
          <Text style={[styles.salaryTrend, { color: '#059669' }]}>{b.trend}</Text>
        </View>
      ))}

      {/* Candidate sentiment */}
      <Text style={styles.h2}>Candidate sentiment</Text>
      <View style={styles.sentimentRow}>
        {UK_MARKET_2025.sentiments.map((s, i) => (
          <View key={i} style={styles.sentimentCard}>
            <Text style={styles.sentimentEmoji}>{s.emoji}</Text>
            <Text style={styles.sentimentLabel}>{s.label}</Text>
            <Text style={styles.sentimentDesc}>{s.desc}</Text>
          </View>
        ))}
      </View>

      {/* Voices from the market */}
      <Text style={styles.h2}>Voices from the market</Text>
      {UK_MARKET_2025.quotes.map((q, i) => (
        <View key={i} style={styles.quoteCard}>
          <MaterialCommunityIcons name="format-quote-open" size={20} color="#0e4e96" />
          <Text style={styles.quoteText}>{q.text}</Text>
          <View style={styles.quoteFooter}>
            <Text style={styles.quoteSub}>{q.sub}</Text>
            <Text style={styles.quoteTopic}>{q.topic}</Text>
          </View>
        </View>
      ))}

      {/* ───── Unemployment trend (8 quarters) ───── */}
      <Text style={styles.h2}>UK unemployment trend</Text>
      <TrendChart
        data={UNEMPLOYMENT_TREND}
        color="#dc2626"
        suffix="%"
        caption="Quarterly UK unemployment rate — Q1 24 to Q4 25"
      />

      {/* ───── Vacancies trend ───── */}
      <Text style={styles.h2}>UK vacancies trend</Text>
      <TrendChart
        data={VACANCIES_TREND}
        color="#2563eb"
        suffix="k"
        caption="Quarterly vacancy stock (000s) — Q1 24 to Q4 25"
      />

      {/* ───── Live community news ───── */}
      <View style={styles.newsHeaderRow}>
        <Text style={[styles.h2, { marginBottom: 0 }]}>From the community</Text>
        {newsLoading
          ? <ActivityIndicator size="small" color="#0e4e96" />
          : (
            <TouchableOpacity onPress={fetchNews} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="refresh" size={16} color="#0e4e96" />
            </TouchableOpacity>
          )
        }
      </View>
      <Text style={styles.newsSub}>
        Live discussions from {NEWS_SUBREDDITS.map(s => s.label).join(' · ')}
      </Text>

      {newsError && newsItems.length === 0 && (
        <EmptyState
          icon="cloud-offline-outline"
          message="Could not reach Reddit. Pull to refresh or check your connection."
        />
      )}

      {newsItems.map((n) => (
        <TouchableOpacity
          key={n.id}
          style={styles.newsCard}
          onPress={() => Linking.openURL(n.url).catch(() => {})}
        >
          <View style={styles.newsCardHeaderRow}>
            <View style={styles.newsSubBadge}>
              <Text style={styles.newsSubBadgeText}>{n.sub}</Text>
            </View>
            <Text style={styles.newsTime}>{formatRelative(n.created)}</Text>
          </View>
          <Text style={styles.newsTitle} numberOfLines={3}>{n.title}</Text>
          <View style={styles.newsCardFooter}>
            <View style={styles.newsMeta}>
              <Ionicons name="arrow-up" size={11} color="#64748b" />
              <Text style={styles.newsMetaText}>{n.score}</Text>
            </View>
            <View style={styles.newsMeta}>
              <Ionicons name="chatbubble-outline" size={11} color="#64748b" />
              <Text style={styles.newsMetaText}>{n.comments}</Text>
            </View>
            <Text style={styles.newsMetaText}>by {n.author}</Text>
            <Ionicons name="open-outline" size={12} color="#0e4e96" style={{ marginLeft: 'auto' }} />
          </View>
        </TouchableOpacity>
      ))}

      {/* ───── Curated research sources ───── */}
      <Text style={styles.h2}>Trusted research sources</Text>
      <Text style={styles.newsSub}>
        Tap to open. These are the UK labour-market sites professional analysts use.
      </Text>

      {/* Group by category */}
      {['Official', 'Industry', 'Professional', 'Job Board', 'News'].map((cat) => {
        const items = RESEARCH_SOURCES.filter(s => s.category === cat);
        if (items.length === 0) return null;
        return (
          <View key={cat} style={{ marginTop: 12 }}>
            <Text style={styles.sourceCategory}>{cat}</Text>
            {items.map(src => (
              <TouchableOpacity
                key={src.id}
                style={styles.researchCard}
                onPress={() => Linking.openURL(src.url).catch(() => {})}
              >
                <View style={[styles.researchIcon, { backgroundColor: src.color + '15' }]}>
                  <Ionicons name={src.icon} size={18} color={src.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.researchHeaderRow}>
                    <Text style={styles.researchName}>{src.name}</Text>
                    <Ionicons name="open-outline" size={13} color="#0e4e96" />
                  </View>
                  <Text style={styles.researchOrg}>{src.org}</Text>
                  <Text style={styles.researchDesc} numberOfLines={2}>{src.desc}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        );
      })}

      {/* Attribution footer */}
      <View style={styles.attributionBox}>
        <Ionicons name="information-circle-outline" size={14} color="#64748b" />
        <Text style={styles.attributionText}>
          Live data: ONS via MarketDataService. Community: public Reddit JSON. Sources open in your browser; JobEng has no affiliation with linked sites.
        </Text>
      </View>
    </ScrollView>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — CHALLENGES
  // ════════════════════════════════════════════════════════════════════════
  const renderChallenges = () => (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
      refreshControl={refreshControl}
    >
      <View style={styles.sectionBanner}>
        <MaterialCommunityIcons name="alert-circle-outline" size={20} color="#fff" />
        <Text style={styles.sectionBannerText}>UK Recruitment Landscape 2025–26</Text>
      </View>

      {visibleChallenges.length === 0 && (
        <EmptyState
          icon="search"
          message={
            showOnlyBookmarked
              ? 'No bookmarked challenges yet — tap the bookmark icon on any card to save it.'
              : 'No challenges match your search.'
          }
        />
      )}

      {visibleChallenges.map((c, i) => {
        const expanded = expandedChallenge === c.id;
        const marked   = isBookmarked(c.id);
        return (
          <View key={c.id} style={[styles.challengeCard, expanded && styles.challengeCardExpanded]}>
            <Pressable
              onPress={() => setExpandedChallenge(expanded ? null : c.id)}
              android_ripple={{ color: 'rgba(0,0,0,0.04)' }}
            >
              <View style={styles.challengeHeader}>
                <View style={[styles.challengeIcon, { backgroundColor: c.color + '18' }]}>
                  <MaterialCommunityIcons name={c.icon} size={22} color={c.color} />
                </View>
                <View style={styles.challengeTitleBox}>
                  <Text style={styles.challengeTitle}>{c.title}</Text>
                  <View style={[styles.impactBadge, { backgroundColor: impactColor(c.impact) + '20' }]}>
                    <Text style={[styles.impactText, { color: impactColor(c.impact) }]}>{c.impact} Impact</Text>
                  </View>
                </View>
                <View style={[styles.statBox, { backgroundColor: c.color + '12' }]}>
                  <Text style={[styles.statNum, { color: c.color }]}>{c.stat}</Text>
                  <Text style={styles.statLbl} numberOfLines={2}>{c.statLabel}</Text>
                </View>
              </View>
            </Pressable>

            {expanded && (
              <View style={styles.challengeBody}>
                <Text style={styles.challengeDesc}>{c.desc}</Text>
                <View style={styles.actionRow}>
                  <TouchableOpacity style={styles.smBtn} onPress={() => openDetail('challenge', c)}>
                    <Ionicons name="reader-outline" size={14} color="#0e4e96" />
                    <Text style={styles.smBtnText}>Read more</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.smBtn} onPress={() => shareItem(c)}>
                    <Ionicons name="share-outline" size={14} color="#0e4e96" />
                    <Text style={styles.smBtnText}>Share</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.smBtn} onPress={() => toggleBookmark(c.id)}>
                    <Ionicons name={marked ? 'bookmark' : 'bookmark-outline'} size={14} color="#0e4e96" />
                    <Text style={styles.smBtnText}>{marked ? 'Saved' : 'Save'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
  // ════════════════════════════════════════════════════════════════════════
  // RENDER — STRATEGIES
  // ════════════════════════════════════════════════════════════════════════
  const renderStrategies = () => (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
      refreshControl={refreshControl}
    >
      <View style={[styles.sectionBanner, { backgroundColor: '#f59e0b' }]}>
        <MaterialCommunityIcons name="lightbulb-outline" size={20} color="#fff" />
        <Text style={styles.sectionBannerText}>Strategies to Overcome Challenges</Text>
      </View>

      {visibleStrategies.length === 0 && (
        <EmptyState icon="search" message={showOnlyBookmarked ? 'No bookmarked strategies yet.' : 'No strategies match your search.'} />
      )}

      <View style={styles.stratGrid}>
        {visibleStrategies.map((s) => {
          const marked = isBookmarked(s.id);
          return (
            <TouchableOpacity
              key={s.id}
              style={[styles.stratCard, { borderTopColor: s.color }]}
              onPress={() => openDetail('strategy', s)}
              activeOpacity={0.85}
            >
              <View style={styles.stratHeaderRow}>
                <View style={[styles.stratIcon, { backgroundColor: s.color + '15' }]}>
                  <Ionicons name={s.icon} size={24} color={s.color} />
                </View>
                <TouchableOpacity onPress={() => toggleBookmark(s.id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                  <Ionicons name={marked ? 'bookmark' : 'bookmark-outline'} size={16} color={s.color} />
                </TouchableOpacity>
              </View>
              <View style={[styles.stratTag, { backgroundColor: s.color + '20' }]}>
                <Text style={[styles.stratTagText, { color: s.color }]}>{s.tag}</Text>
              </View>
              <Text style={styles.stratTitle}>{s.title}</Text>
              <Text style={styles.stratDesc}>{s.desc}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* ═══ BEST PRACTICES (merged from former Best Practice tab) ══════ */}
      {visiblePractices.length > 0 && (
        <>
          <View style={[styles.sectionBanner, { backgroundColor: '#7c3aed', marginTop: 16 }]}>
            <MaterialCommunityIcons name="star-outline" size={20} color="#fff" />
            <Text style={styles.sectionBannerText}>Best Practices from UK Recruiters</Text>
          </View>

          {visiblePractices.map((bp, i) => {
            const expanded = expandedBP === i;
            const marked   = isBookmarked(bp.id);
            return (
              <View key={bp.id} style={[styles.bpCard, { borderLeftColor: bp.color }]}>
                <TouchableOpacity style={styles.bpHeader} onPress={() => setExpandedBP(expanded ? -1 : i)}>
                  <View style={[styles.bpNum, { backgroundColor: bp.color }]}>
                    <Text style={styles.bpNumText}>{bp.number}</Text>
                  </View>
                  <View style={styles.bpTitleBox}>
                    <Ionicons name={bp.icon} size={16} color={bp.color} style={{ marginBottom: 2 }} />
                    <Text style={styles.bpTitle}>{bp.title}</Text>
                  </View>
                  <TouchableOpacity onPress={() => toggleBookmark(bp.id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                    <Ionicons name={marked ? 'bookmark' : 'bookmark-outline'} size={18} color={bp.color} />
                  </TouchableOpacity>
                  <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color="#94a3b8" />
                </TouchableOpacity>

                {expanded && (
                  <View style={styles.bpBody}>
                    <Text style={styles.bpLong}>{bp.longDesc}</Text>
                    {bp.items.map((item, j) => (
                      <View key={j} style={styles.bpItem}>
                        <View style={[styles.bpItemDot, { backgroundColor: bp.color }]}>
                          <Ionicons name={item.icon} size={12} color="#fff" />
                        </View>
                        <Text style={styles.bpItemText}>{item.text}</Text>
                      </View>
                    ))}
                    <View style={styles.actionRow}>
                      <TouchableOpacity style={styles.smBtn} onPress={() => openDetail('practice', bp)}>
                        <Ionicons name="reader-outline" size={14} color="#0e4e96" />
                        <Text style={styles.smBtnText}>Read more</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.smBtn} onPress={() => shareItem(bp)}>
                        <Ionicons name="share-outline" size={14} color="#0e4e96" />
                        <Text style={styles.smBtnText}>Share</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </View>
            );
          })}

          <View style={styles.sourceBox}>
            <Ionicons name="logo-reddit" size={14} color="#ff4500" />
            <Text style={styles.sourceText}>Curated from r/recruiting community + CIPD/LinkedIn reports</Text>
          </View>
        </>
      )}
    </ScrollView>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — JOBENG FIX (with deep-links + bookmark + detail)
  // ════════════════════════════════════════════════════════════════════════
  const renderJobEngFix = () => (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
      refreshControl={refreshControl}
    >
      <View style={[styles.sectionBanner, { backgroundColor: '#0e4e96' }]}>
        <MaterialCommunityIcons name="shield-check-outline" size={20} color="#fff" />
        <Text style={styles.sectionBannerText}>How JobEng Solves These Challenges</Text>
      </View>

      {visibleFixes.length === 0 && (
        <EmptyState icon="search" message={showOnlyBookmarked ? 'No bookmarked fixes yet.' : 'No fixes match your search.'} />
      )}

      {visibleFixes.map((fix) => {
        const marked = isBookmarked(fix.id);
        return (
          <View key={fix.id} style={styles.fixCard}>
            <View style={styles.fixHeader}>
              <View style={[styles.fixIcon, { backgroundColor: fix.color }]}>
                <MaterialCommunityIcons name={fix.icon} size={20} color="#fff" />
              </View>
              <View style={styles.fixTitleBox}>
                <Text style={styles.fixChallenge}>Challenge: {fix.challenge}</Text>
                <Text style={styles.fixDesc}>{fix.desc}</Text>
              </View>
              <TouchableOpacity onPress={() => toggleBookmark(fix.id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Ionicons name={marked ? 'bookmark' : 'bookmark-outline'} size={18} color={fix.color} />
              </TouchableOpacity>
            </View>
            <View style={styles.fixFeatures}>
              {fix.features.map((f, j) => (
                <View key={j} style={[styles.fixChip, { backgroundColor: fix.color + '12', borderColor: fix.color + '40' }]}>
                  <Ionicons name="checkmark-circle" size={12} color={fix.color} />
                  <Text style={[styles.fixChipText, { color: fix.color }]}>{f}</Text>
                </View>
              ))}
            </View>
            <View style={styles.actionRow}>
              <TouchableOpacity style={[styles.smBtn, { backgroundColor: fix.color + '12' }]} onPress={() => openFixScreen(fix.route)}>
                <Ionicons name="open-outline" size={14} color={fix.color} />
                <Text style={[styles.smBtnText, { color: fix.color }]}>Open feature</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.smBtn} onPress={() => openDetail('fix', fix)}>
                <Ionicons name="reader-outline" size={14} color="#0e4e96" />
                <Text style={styles.smBtnText}>Read more</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.smBtn} onPress={() => shareItem(fix)}>
                <Ionicons name="share-outline" size={14} color="#0e4e96" />
                <Text style={styles.smBtnText}>Share</Text>
              </TouchableOpacity>
            </View>
          </View>
        );
      })}

      <TouchableOpacity style={styles.upgradeCard} onPress={() => openFixScreen('BusinessDashboard')}>
        <MaterialCommunityIcons name="rocket-launch" size={28} color="#fff" />
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={styles.upgradeTitle}>Ready to transform your hiring?</Text>
          <Text style={styles.upgradeSub}>Access all these features in your Business Dashboard</Text>
        </View>
        <Ionicons name="arrow-forward" size={20} color="#fff" />
      </TouchableOpacity>
    </ScrollView>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — tab router
  // ════════════════════════════════════════════════════════════════════════
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':   return renderOverview();
      case 'challenges': return renderChallenges();
      case 'strategies': return renderStrategies();
      case 'jobeng':     return renderJobEngFix();
      default:           return renderOverview();
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — ROOT
  // ════════════════════════════════════════════════════════════════════════
  return (
    <SafeAreaView style={styles.safe}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation?.goBack?.()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Market Insights</Text>
          <Text style={styles.headerSub}>UK Recruitment Intelligence 2025–26</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity
            onPress={() => setSearchOpen(s => !s)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={styles.headerIconBtn}
          >
            <Ionicons name={searchOpen ? 'close' : 'search'} size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setShowOnlyBookmarked(v => !v)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={styles.headerIconBtn}
          >
            <Ionicons
              name={showOnlyBookmarked ? 'bookmark' : 'bookmark-outline'}
              size={20}
              color={showOnlyBookmarked ? '#fbbf24' : '#fff'}
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Search */}
      {searchOpen && (
        <View style={styles.searchBox}>
          <Ionicons name="search" size={16} color="#64748b" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search insights, challenges, strategies…"
            placeholderTextColor="#94a3b8"
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoFocus
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={18} color="#94a3b8" />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Active filter banner */}
      {showOnlyBookmarked && (
        <View style={styles.activeFilterBar}>
          <Ionicons name="bookmark" size={14} color="#fff" />
          <Text style={styles.activeFilterText}>Showing only bookmarks</Text>
          <TouchableOpacity onPress={() => setShowOnlyBookmarked(false)}>
            <Ionicons name="close" size={16} color="#fff" />
          </TouchableOpacity>
        </View>
      )}

      {/* KPI strip */}
      <View style={styles.kpiStrip}>
        {[
          { val: String(CHALLENGES.length),                       label: 'Challenges' },
          { val: String(STRATEGIES.length + BEST_PRACTICES.length), label: 'Strategies' },
          { val: String(JOBENG_FIXES.length),                     label: 'Fixes' },
          { val: String(UK_MARKET_2025.regions.length),           label: 'Regions' },
        ].map((k, i) => (
          <View key={i} style={styles.kpiItem}>
            <Text style={styles.kpiVal}>{k.val}</Text>
            <Text style={styles.kpiLabel}>{k.label}</Text>
          </View>
        ))}
      </View>

      {/* Tabs — 4 tabs fit comfortably without horizontal scroll */}
      <View style={styles.tabBar}>
        {TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <TouchableOpacity
              key={t.key}
              style={[styles.tab, active && styles.tabActive]}
              onPress={() => switchTab(t.key)}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons
                name={t.icon}
                size={14}
                color={active ? '#0e4e96' : '#64748b'}
                style={{ marginRight: 4 }}
              />
              <Text style={[styles.tabText, active && styles.tabTextActive]}>{t.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Content */}
      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        {renderContent()}
      </Animated.View>

      {/* Detail Modal */}
      <DetailModal
        item={detailItem}
        onClose={closeDetail}
        onShare={shareItem}
        onBookmark={toggleBookmark}
        isBookmarked={isBookmarked}
        onOpenRoute={openFixScreen}
      />
    </SafeAreaView>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

function EmptyState({ icon = 'search', message }) {
  return (
    <View style={styles.emptyState}>
      <Ionicons name={icon} size={36} color="#94a3b8" />
      <Text style={styles.emptyText}>{message}</Text>
    </View>
  );
}

// Minimal pure-RN bar chart. No chart library needed; widths/heights are
// computed from the data range so it works at any width.
function TrendChart({ data, color = '#0e4e96', suffix = '', caption }) {
  if (!data || data.length === 0) return null;
  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const lastIdx = data.length - 1;
  const direction = data[lastIdx].value - data[0].value;

  return (
    <View style={styles.chartCard}>
      <View style={styles.chartHeader}>
        <View>
          <Text style={styles.chartLatest}>{data[lastIdx].value}{suffix}</Text>
          <Text style={styles.chartLatestLbl}>Latest · {data[lastIdx].label}</Text>
        </View>
        <View style={[
          styles.chartDeltaBadge,
          { backgroundColor: direction >= 0 ? '#fee2e2' : '#dcfce7' },
        ]}>
          <Ionicons
            name={direction >= 0 ? 'trending-up' : 'trending-down'}
            size={11}
            color={direction >= 0 ? '#dc2626' : '#059669'}
          />
          <Text style={[
            styles.chartDeltaText,
            { color: direction >= 0 ? '#dc2626' : '#059669' },
          ]}>
            {direction >= 0 ? '+' : ''}{direction.toFixed(1)}{suffix} vs {data[0].label}
          </Text>
        </View>
      </View>

      <View style={styles.chartBars}>
        {data.map((d, i) => {
          const heightPct = ((d.value - min) / span) * 100;
          const minBarPct = 8; // ensure non-zero bars are still visible
          const finalPct = Math.max(minBarPct, heightPct);
          const isLast = i === lastIdx;
          return (
            <View key={i} style={styles.chartBarCol}>
              <View style={styles.chartBarWrap}>
                <View style={[
                  styles.chartBar,
                  {
                    height: `${finalPct}%`,
                    backgroundColor: isLast ? color : color + '55',
                  },
                ]} />
              </View>
              <Text style={[styles.chartLabel, isLast && { color, fontWeight: '700' }]}>
                {d.label}
              </Text>
            </View>
          );
        })}
      </View>

      {caption && <Text style={styles.chartCaption}>{caption}</Text>}
    </View>
  );
}

function DetailModal({ item, onClose, onShare, onBookmark, isBookmarked, onOpenRoute }) {
  if (!item) return null;
  const { kind, data } = item;
  if (!data) return null;

  const title    = data.title || data.challenge || 'Insight';
  const longDesc = data.longDesc || data.desc || '';
  const sources  = data.sources || [];
  const actions  = data.actionItems || [];
  const related  = data.related || data.features || [];
  const color    = data.color || '#0e4e96';
  const marked   = isBookmarked(data.id);

  return (
    <Modal visible={true} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalSheet}>
          <View style={styles.modalGrabber} />
          <View style={styles.modalHeaderRow}>
            <View style={[styles.modalKindBadge, { backgroundColor: color + '15' }]}>
              <Text style={[styles.modalKindText, { color }]}>{kind.toUpperCase()}</Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={22} color="#64748b" />
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.modalTitle}>{title}</Text>

            {data.stat && (
              <View style={[styles.modalStatBox, { backgroundColor: color + '10' }]}>
                <Text style={[styles.modalStat, { color }]}>{data.stat}</Text>
                <Text style={styles.modalStatLabel}>{data.statLabel}</Text>
              </View>
            )}

            <Text style={styles.modalBody}>{longDesc}</Text>

            {actions.length > 0 && (
              <>
                <Text style={styles.modalSectionTitle}>Action items</Text>
                {actions.map((a, i) => (
                  <View key={i} style={styles.modalActionRow}>
                    <View style={[styles.modalActionDot, { backgroundColor: color }]}>
                      <Text style={styles.modalActionNum}>{i + 1}</Text>
                    </View>
                    <Text style={styles.modalActionText}>{a}</Text>
                  </View>
                ))}
              </>
            )}

            {related.length > 0 && (
              <>
                <Text style={styles.modalSectionTitle}>Related features</Text>
                <View style={styles.fixFeatures}>
                  {related.map((r, i) => (
                    <View key={i} style={[styles.fixChip, { backgroundColor: color + '12', borderColor: color + '40' }]}>
                      <Ionicons name="link" size={12} color={color} />
                      <Text style={[styles.fixChipText, { color }]}>{r}</Text>
                    </View>
                  ))}
                </View>
              </>
            )}

            {sources.length > 0 && (
              <>
                <Text style={styles.modalSectionTitle}>Sources</Text>
                {sources.map((s, i) => (
                  <TouchableOpacity
                    key={i}
                    style={styles.modalSourceRow}
                    onPress={() => s.url && Linking.openURL(s.url).catch(() => {})}
                  >
                    <Ionicons name="link-outline" size={14} color="#0e4e96" />
                    <Text style={styles.modalSourceText}>{s.label}</Text>
                    {s.url && <Ionicons name="open-outline" size={12} color="#0e4e96" />}
                  </TouchableOpacity>
                ))}
              </>
            )}

            {data.route && (
              <TouchableOpacity
                style={[styles.modalCta, { backgroundColor: color }]}
                onPress={() => { onClose(); onOpenRoute(data.route); }}
              >
                <Ionicons name="arrow-forward-circle" size={20} color="#fff" />
                <Text style={styles.modalCtaText}>Open {data.route}</Text>
              </TouchableOpacity>
            )}
          </ScrollView>

          {/* Footer actions */}
          <View style={styles.modalFooter}>
            <TouchableOpacity style={styles.modalFooterBtn} onPress={() => onBookmark(data.id)}>
              <Ionicons name={marked ? 'bookmark' : 'bookmark-outline'} size={18} color="#0e4e96" />
              <Text style={styles.modalFooterText}>{marked ? 'Saved' : 'Save'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.modalFooterBtn} onPress={() => onShare(data)}>
              <Ionicons name="share-outline" size={18} color="#0e4e96" />
              <Text style={styles.modalFooterText}>Share</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.modalFooterBtn, styles.modalFooterBtnPrimary]} onPress={onClose}>
              <Text style={styles.modalFooterTextPrimary}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STYLES
// ════════════════════════════════════════════════════════════════════════════
const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },

  // Header
  header: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96',
    paddingHorizontal: 16, paddingVertical: 12, paddingTop: 14,
  },
  backBtn:       { padding: 4 },
  headerCenter:  { flex: 1, alignItems: 'center' },
  headerTitle:   { color: '#fff', fontSize: 17, fontWeight: '700' },
  headerSub:     { color: '#cbd5e1', fontSize: 11, marginTop: 2 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  headerIconBtn: { padding: 4 },

  // Search
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#fff', marginHorizontal: 12, marginTop: 8,
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  searchInput: { flex: 1, fontSize: 14, color: '#0f172a', paddingVertical: 0 },

  // Active filter
  activeFilterBar: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#0e4e96', paddingHorizontal: 12, paddingVertical: 6,
    marginHorizontal: 12, marginTop: 6, borderRadius: 8,
  },
  activeFilterText: { color: '#fff', fontSize: 12, fontWeight: '600', flex: 1 },

  // KPI strip
  kpiStrip: {
    flexDirection: 'row', backgroundColor: '#fff',
    paddingVertical: 12, marginTop: 8, marginHorizontal: 12, borderRadius: 8,
    shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 4, shadowOffset: { height: 1, width: 0 }, elevation: 1,
  },
  kpiItem:  { flex: 1, alignItems: 'center' },
  kpiVal:   { fontSize: 18, fontWeight: '700', color: '#0e4e96' },
  kpiLabel: { fontSize: 10, color: '#64748b', marginTop: 2, fontWeight: '500' },

  // Tabs
  tabBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    marginTop: 6,
    marginHorizontal: 12,
    borderRadius: 8,
    paddingHorizontal: 4,
    paddingVertical: 4,
    height: 44,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 6,
    borderRadius: 6,
    marginHorizontal: 2,
    height: 34,
  },
  tabActive:    { backgroundColor: '#dbeafe' },
  tabText:      { fontSize: 12, color: '#64748b', fontWeight: '600' },
  tabTextActive:{ color: '#0e4e96' },

  // Section banner
  sectionBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#ef4444', paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: 6, marginBottom: 12,
  },
  sectionBannerText: { color: '#fff', fontSize: 12, fontWeight: '700', flex: 1 },

  h2: { fontSize: 14, fontWeight: '700', color: '#0f172a', marginTop: 16, marginBottom: 8 },

  // Hero stats (Overview)
  heroStatsRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  heroStat: {
    flex: 1, backgroundColor: '#fff', borderRadius: 8, padding: 12,
    borderTopWidth: 3, alignItems: 'center', position: 'relative',
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { height: 1, width: 0 }, elevation: 1,
  },
  heroStatVal:   { fontSize: 20, fontWeight: '800' },
  heroStatLabel: { fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 4, fontWeight: '500' },
  liveDotInline: { width: 6, height: 6, borderRadius: 3, position: 'absolute', top: 8, right: 8 },

  // Freshness
  freshnessBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#fff', padding: 10, borderRadius: 8, marginBottom: 12,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  freshnessText: { flex: 1, fontSize: 11, color: '#475569', fontWeight: '500' },
  freshnessTime: { fontSize: 10, color: '#94a3b8' },

  // Quick grid (Overview)
  quickGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  quickTile: {
    width: (width - 32 - 8) / 2, backgroundColor: '#fff', borderRadius: 8, padding: 12,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  quickIcon:  { width: 36, height: 36, borderRadius: 8, alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  quickLabel: { fontSize: 13, fontWeight: '700', color: '#0f172a' },
  quickCount: { fontSize: 11, color: '#64748b', marginTop: 2 },

  // Top picks (Overview)
  topCard: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 8,
    padding: 12, marginBottom: 8, borderWidth: 1, borderColor: '#e2e8f0', gap: 8, overflow: 'hidden',
  },
  topCardBar:   { width: 3, alignSelf: 'stretch', borderRadius: 2 },
  topCardLabel: { fontSize: 9, fontWeight: '700', color: '#64748b', letterSpacing: 0.6 },
  topCardTitle: { fontSize: 13, fontWeight: '700', color: '#0f172a', marginTop: 2 },
  topCardDesc:  { fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 15 },

  // Bookmark summary
  bookmarkSummary: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#dbeafe', padding: 12, borderRadius: 8, marginTop: 12,
  },
  bookmarkSummaryText: { flex: 1, fontSize: 12, fontWeight: '600', color: '#0e4e96' },

  // Challenge cards
  challengeCard: {
    backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 8,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  challengeCardExpanded: { borderColor: '#0e4e96' },
  challengeHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  challengeIcon:   { width: 36, height: 36, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  challengeTitleBox: { flex: 1 },
  challengeTitle:  { fontSize: 13, fontWeight: '700', color: '#0f172a' },
  impactBadge:     { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, alignSelf: 'flex-start', marginTop: 4 },
  impactText:      { fontSize: 9, fontWeight: '700' },
  statBox:         { paddingHorizontal: 8, paddingVertical: 6, borderRadius: 6, alignItems: 'center', minWidth: 60 },
  statNum:         { fontSize: 16, fontWeight: '800' },
  statLbl:         { fontSize: 8, color: '#64748b', textAlign: 'center', marginTop: 2, lineHeight: 10 },
  challengeBody:   { marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#e2e8f0' },
  challengeDesc:   { fontSize: 12, color: '#475569', lineHeight: 17 },

  // Small action buttons (universal)
  actionRow: { flexDirection: 'row', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  smBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 10, paddingVertical: 6, backgroundColor: '#f1f5f9', borderRadius: 6,
  },
  smBtnText: { fontSize: 11, fontWeight: '600', color: '#0e4e96' },

  // Best Practice
  bpCard: {
    backgroundColor: '#fff', borderRadius: 8, marginBottom: 8,
    borderLeftWidth: 3, borderWidth: 1, borderColor: '#e2e8f0', overflow: 'hidden',
  },
  bpHeader:    { flexDirection: 'row', alignItems: 'center', padding: 12, gap: 10 },
  bpNum:       { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  bpNumText:   { color: '#fff', fontSize: 13, fontWeight: '700' },
  bpTitleBox:  { flex: 1 },
  bpTitle:     { fontSize: 13, fontWeight: '700', color: '#0f172a' },
  bpBody:      { padding: 12, paddingTop: 0, gap: 8 },
  bpLong:      { fontSize: 12, color: '#475569', lineHeight: 17, marginBottom: 6 },
  bpItem:      { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  bpItemDot:   { width: 18, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  bpItemText:  { flex: 1, fontSize: 12, color: '#475569', lineHeight: 17 },

  // Strategies grid
  stratGrid:        { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  stratCard:        {
    width: (width - 32 - 8) / 2, backgroundColor: '#fff', borderRadius: 8, padding: 12,
    borderTopWidth: 3, borderWidth: 1, borderColor: '#e2e8f0',
  },
  stratHeaderRow:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  stratIcon:        { width: 40, height: 40, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  stratTag:         { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, alignSelf: 'flex-start', marginBottom: 6 },
  stratTagText:     { fontSize: 9, fontWeight: '700' },
  stratTitle:       { fontSize: 13, fontWeight: '700', color: '#0f172a' },
  stratDesc:        { fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 15 },

  // UK Market
  ukHeadlineBox: { backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  ukStatRow:     { flexDirection: 'row', gap: 8 },
  ukStat:        { flex: 1, padding: 10, borderRadius: 6, alignItems: 'center' },
  ukStatNum:     { fontSize: 18, fontWeight: '800' },
  ukStatLabel:   { fontSize: 9, color: '#64748b', textAlign: 'center', marginTop: 4, fontWeight: '500' },

  liveStatusBar: { marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#e2e8f0' },
  liveStatusRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  liveDot:       { width: 8, height: 8, borderRadius: 4 },
  liveStatusText:{ fontSize: 10, color: '#475569', fontWeight: '500' },
  liveStatusTime:{ fontSize: 10, color: '#94a3b8', marginLeft: 'auto' },

  ukSentimentBadge: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#fee2e2', padding: 8, borderRadius: 6, marginTop: 10 },
  ukSentimentText:  { fontSize: 11, color: '#dc2626', fontWeight: '600', flex: 1 },

  sectorScrollWrap: { marginBottom: 4 },
  sectorCard: {
    width: 140, backgroundColor: '#fff', borderRadius: 8, padding: 10, marginRight: 8,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  sectorName:       { fontSize: 11, fontWeight: '700', color: '#0f172a', minHeight: 28 },
  sectorValue:      { fontSize: 16, fontWeight: '800', color: '#0e4e96', marginTop: 4 },
  sectorChange:     { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 3, borderRadius: 4, alignSelf: 'flex-start', marginTop: 6 },
  sectorChangeText: { fontSize: 10, fontWeight: '700' },

  regionGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  regionCard: {
    width: (width - 32 - 8) / 2, backgroundColor: '#fff', borderRadius: 8, padding: 10,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  regionHeader: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 6 },
  regionName:   { fontSize: 12, fontWeight: '700', color: '#0f172a' },
  regionRow:    { flexDirection: 'row', justifyContent: 'space-between', marginTop: 2 },
  regionLabel:  { fontSize: 10, color: '#64748b' },
  regionVal:    { fontSize: 11, fontWeight: '700', color: '#0f172a' },
  regionHotRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 8 },
  regionHot:    { backgroundColor: '#dbeafe', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  regionHotText:{ fontSize: 9, color: '#0e4e96', fontWeight: '600' },

  skillRow: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff',
    padding: 10, borderRadius: 8, marginBottom: 6, borderWidth: 1, borderColor: '#e2e8f0',
  },
  skillName:      { fontSize: 12, fontWeight: '700', color: '#0f172a' },
  skillSalary:    { fontSize: 11, color: '#64748b', marginTop: 2 },
  skillTrend:     { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: '#dcfce7', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  skillTrendText: { fontSize: 11, fontWeight: '700', color: '#059669' },

  salaryRow: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff',
    padding: 10, borderRadius: 8, marginBottom: 6, borderWidth: 1, borderColor: '#e2e8f0', gap: 8,
  },
  salaryRole:      { fontSize: 12, fontWeight: '700', color: '#0f172a' },
  salaryRange:     { fontSize: 11, color: '#64748b', marginTop: 2 },
  salaryMedianBox: { alignItems: 'flex-end' },
  salaryMedianLbl: { fontSize: 9, color: '#94a3b8' },
  salaryMedian:    { fontSize: 13, fontWeight: '800', color: '#0e4e96' },
  salaryTrend:     { fontSize: 11, fontWeight: '700', minWidth: 30, textAlign: 'right' },

  ukProblemCard:    { backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  ukProblemHeader:  { flexDirection: 'row', gap: 10, marginBottom: 8 },
  ukProblemMeta:    { flexDirection: 'row', gap: 4, marginTop: 4 },
  subredditBadge:   { backgroundColor: '#ffedd5', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  subredditText:    { fontSize: 9, color: '#9a3412', fontWeight: '700' },

  sentimentRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  sentimentCard: {
    width: (width - 32 - 8) / 2, backgroundColor: '#fff', borderRadius: 8, padding: 12,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  sentimentEmoji: { fontSize: 24 },
  sentimentLabel: { fontSize: 12, fontWeight: '700', color: '#0f172a', marginTop: 4 },
  sentimentDesc:  { fontSize: 10, color: '#64748b', marginTop: 4, lineHeight: 14 },

  quoteCard: {
    backgroundColor: '#fff', padding: 12, borderRadius: 8, marginBottom: 8,
    borderLeftWidth: 3, borderLeftColor: '#0e4e96', borderWidth: 1, borderColor: '#e2e8f0',
  },
  quoteText:   { fontSize: 12, color: '#0f172a', fontStyle: 'italic', marginTop: 4, lineHeight: 17 },
  quoteFooter: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  quoteSub:    { fontSize: 10, fontWeight: '700', color: '#0e4e96' },
  quoteTopic:  { fontSize: 10, color: '#64748b' },

  // JobEng Fix
  fixCard:      { backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  fixHeader:    { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  fixIcon:      { width: 36, height: 36, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  fixTitleBox:  { flex: 1 },
  fixChallenge: { fontSize: 10, fontWeight: '700', color: '#64748b', letterSpacing: 0.6 },
  fixDesc:      { fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 17 },
  fixFeatures:  { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 10 },
  fixChip:      { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, borderWidth: 1 },
  fixChipText:  { fontSize: 10, fontWeight: '600' },

  upgradeCard: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96',
    padding: 14, borderRadius: 8, marginTop: 8,
  },
  upgradeTitle: { color: '#fff', fontSize: 13, fontWeight: '700' },
  upgradeSub:   { color: '#cbd5e1', fontSize: 11, marginTop: 2 },

  // Source / Empty
  sourceBox: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 },
  sourceText:{ fontSize: 10, color: '#64748b', fontStyle: 'italic' },

  emptyState: { alignItems: 'center', paddingVertical: 40, gap: 8 },
  emptyText:  { fontSize: 12, color: '#94a3b8', textAlign: 'center', paddingHorizontal: 32 },

  // Trend chart
  chartCard: {
    backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 8,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  chartHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  chartLatest: { fontSize: 24, fontWeight: '800', color: '#0f172a' },
  chartLatestLbl: { fontSize: 10, color: '#64748b', marginTop: 2 },
  chartDeltaBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4,
  },
  chartDeltaText: { fontSize: 10, fontWeight: '700' },
  chartBars: { flexDirection: 'row', alignItems: 'flex-end', height: 90, gap: 4, marginBottom: 4 },
  chartBarCol: { flex: 1, alignItems: 'center', height: '100%' },
  chartBarWrap: { flex: 1, width: '100%', justifyContent: 'flex-end', alignItems: 'center' },
  chartBar: { width: '70%', borderTopLeftRadius: 3, borderTopRightRadius: 3, minHeight: 4 },
  chartLabel: { fontSize: 9, color: '#94a3b8', marginTop: 4 },
  chartCaption: { fontSize: 10, color: '#94a3b8', marginTop: 6, fontStyle: 'italic' },

  // News feed
  newsHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 4 },
  newsSub: { fontSize: 11, color: '#64748b', marginBottom: 8 },
  newsCard: {
    backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 6,
    borderWidth: 1, borderColor: '#e2e8f0',
  },
  newsCardHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  newsSubBadge:      { backgroundColor: '#ffedd5', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  newsSubBadgeText:  { fontSize: 9, color: '#9a3412', fontWeight: '700' },
  newsTime:          { fontSize: 10, color: '#94a3b8' },
  newsTitle:         { fontSize: 13, fontWeight: '600', color: '#0f172a', lineHeight: 18 },
  newsCardFooter:    { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8 },
  newsMeta:          { flexDirection: 'row', alignItems: 'center', gap: 3 },
  newsMetaText:      { fontSize: 10, color: '#64748b' },

  // Research sources
  sourceCategory: {
    fontSize: 10, color: '#94a3b8', fontWeight: '700', letterSpacing: 0.8,
    textTransform: 'uppercase', marginBottom: 6,
  },
  researchCard: {
    flexDirection: 'row', backgroundColor: '#fff', borderRadius: 8, padding: 12,
    marginBottom: 6, borderWidth: 1, borderColor: '#e2e8f0', gap: 10,
  },
  researchIcon: { width: 36, height: 36, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  researchHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  researchName:  { fontSize: 13, fontWeight: '700', color: '#0f172a' },
  researchOrg:   { fontSize: 10, color: '#64748b', marginTop: 1 },
  researchDesc:  { fontSize: 11, color: '#475569', marginTop: 4, lineHeight: 15 },

  // Attribution
  attributionBox: {
    flexDirection: 'row', gap: 6, backgroundColor: '#f1f5f9',
    padding: 10, borderRadius: 8, marginTop: 12,
  },
  attributionText: { flex: 1, fontSize: 10, color: '#475569', lineHeight: 14, fontStyle: 'italic' },

  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'flex-end' },
  modalSheet: {
    backgroundColor: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16,
    padding: 16, paddingBottom: 24, maxHeight: '85%',
  },
  modalGrabber: { width: 40, height: 4, backgroundColor: '#cbd5e1', borderRadius: 2, alignSelf: 'center', marginBottom: 8 },
  modalHeaderRow:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  modalKindBadge:  { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  modalKindText:   { fontSize: 9, fontWeight: '700', letterSpacing: 0.8 },
  modalTitle:      { fontSize: 18, fontWeight: '800', color: '#0f172a', marginBottom: 12 },
  modalStatBox:    { padding: 12, borderRadius: 8, alignItems: 'center', marginBottom: 12 },
  modalStat:       { fontSize: 28, fontWeight: '800' },
  modalStatLabel:  { fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 2 },
  modalBody:       { fontSize: 13, color: '#334155', lineHeight: 19, marginBottom: 12 },
  modalSectionTitle:{ fontSize: 12, fontWeight: '700', color: '#0e4e96', marginTop: 12, marginBottom: 6, letterSpacing: 0.4 },
  modalActionRow:  { flexDirection: 'row', gap: 8, marginBottom: 6, alignItems: 'flex-start' },
  modalActionDot:  { width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  modalActionNum:  { color: '#fff', fontSize: 10, fontWeight: '800' },
  modalActionText: { flex: 1, fontSize: 12, color: '#334155', lineHeight: 17 },
  modalSourceRow:  { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6 },
  modalSourceText: { flex: 1, fontSize: 12, color: '#0e4e96', fontWeight: '500' },
  modalCta:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, borderRadius: 8, marginTop: 16 },
  modalCtaText:    { color: '#fff', fontSize: 13, fontWeight: '700' },
  modalFooter:     { flexDirection: 'row', gap: 8, marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: '#e2e8f0' },
  modalFooterBtn:  { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: 10, backgroundColor: '#f1f5f9', borderRadius: 6 },
  modalFooterBtnPrimary: { backgroundColor: '#0e4e96' },
  modalFooterText:        { fontSize: 12, color: '#0e4e96', fontWeight: '700' },
  modalFooterTextPrimary: { fontSize: 12, color: '#fff', fontWeight: '700' },
});
